import axios from 'axios'
import store from '@/store'

// Base API (prod): /api (via nginx reverse proxy)
// Tu peux override avec VUE_APP_API_BASE_URL si besoin
const RAW_BASE =
  (process.env.VUE_APP_API_BASE_URL || '/api')

// on garantit un slash final => "/api/"
const API_BASE = String(RAW_BASE).replace(/\/+$/, '') + '/'

const axiosAgent = axios.create({
  baseURL: API_BASE,
  withCredentials: false, // tu utilises JWT côté header, pas cookie serveur
  timeout: 20020000
})

// Instance dédiée IA (plus longue)
const aiApi = axios.create({
  baseURL: API_BASE,
  timeout: 22120000 // 2 minutes
})

// Helper pour choisir la bonne instance selon le tag
function getClientByTag(tag) {
  // tu peux ajouter d'autres tags IA si besoin
  if (tag === 'CONVERSE') return aiApi
  return api
}

// ---- Interceptor: ajoute automatiquement le Bearer token si présent
axiosAgent.interceptors.request.use((config) => {
  const token = store.getters.userToken || store.state.userToken
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// ---- Normalisation URI: accepte "/users/login" ou "users/login"
function normalizeUri (uri) {
  if (!uri) return ''
  return String(uri).replace(/^\/+/, '') // supprime les / au début
}

function handleError (serviceName, err) {
  // debug utile
  console.error(`❌ ERROR [${serviceName}]`, err)

  if (err.response) {
    return {
      error: 1,
      status: err.response.status,
      data: err.response.data
    }
  }

  if (err.request) {
    return {
      error: 1,
      status: 0,
      data: "Le serveur est injoignable ou l'URL n'existe pas"
    }
  }

  return {
    error: 1,
    status: 0,
    data: 'Erreur inconnue'
  }
}

async function getRequest (uri, name) {
  try {
    const res = await axiosAgent.get(normalizeUri(uri))
    return res.data
  } catch (err) {
    return handleError(name, err)
  }
}

async function postRequest (uri, data, name) {
  try {
    const res = await axiosAgent.post(normalizeUri(uri), data)
    return res.data
  } catch (err) {
    return handleError(name, err)
  }
}

async function patchRequest (uri, data, name) {
  try {
    const res = await axiosAgent.patch(normalizeUri(uri), data)
    return res.data
  } catch (err) {
    return handleError(name, err)
  }
}

async function deleteRequest (uri, name) {
  try {
    const res = await axiosAgent.delete(normalizeUri(uri))
    return res.data
  } catch (err) {
    return handleError(name, err)
  }
}

async function postRequestPicture (uri, image, name) {
  try {
    const formData = new FormData()
    formData.append('photo', image)

    const res = await axiosAgent.post(normalizeUri(uri), formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return res.data
  } catch (err) {
    return handleError(name, err)
  }
}

async function streamAssistantChat(message, game_title = null, onToken) {
  const res = await fetch('/api/assistant/chat/stream', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream'
    },
    body: JSON.stringify({ message, game_title })
  })

  if (!res.ok || !res.body) {
    throw new Error(`HTTP ${res.status}`)
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''

  while (true) {
    const { value, done } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })

    // SSE = lignes, événements séparés par \n\n
    const parts = buffer.split('\n\n')
    buffer = parts.pop() || ''

    for (const part of parts) {
      // on garde seulement les lignes "data: ..."
      const dataLine = part.split('\n').find(l => l.startsWith('data:'))
      if (!dataLine) continue

      const jsonStr = dataLine.replace(/^data:\s*/, '').trim()
      if (!jsonStr || jsonStr === '{}') continue

      try {
        const obj = JSON.parse(jsonStr)
        if (obj.token) onToken(obj.token)
      } catch (e) {
        // ignore parse errors
      }
    }
  }
}

export {
  getRequest,
  postRequest,
  patchRequest,
  deleteRequest,
  postRequestPicture,
  streamAssistantChat
}
